/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.22.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { HttpStatus } from '@modules/app/appConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import ModalErrorCommon from '@modules/modal/components/ModalError';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { wizardNextAction, wizardStorageSaveStepAction } from '@modules/wizard/wizardActions';
import {
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '@modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '@modules/wizard/wizardSagas';
import { makeSelectWizardBasketSnapshotId, makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';

// import { TWizardMetaSuccessAction } from '@modules/wizard/wizardTypes';
import { wizardOrderTemplateSetParamsAction } from './actions';
import { EWizardOrderTemplateSteps, WIZARD_ORDER_TEMPLATE_HANDLE_NEXT } from './constants';

/*
 * -------------------------------------------
 *           INITIALIZING
 * -------------------------------------------
 */

export function* wizardOrderTemplateMetaSaga(/* action: TWizardMetaSuccessAction */): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardOrderTemplateSteps.TemplateSelectionDataStep:
            break;
        case EWizardOrderTemplateSteps.TemplateParametersConfirmStep:
            break;
    }
}

/*
 * -------------------------------------------
 *       SEND DATA ON NEXT STEP
 * -------------------------------------------
 */

export function* wizardOrderTemplateHandleNextSaga(): SagaIterator {
    yield put(wizardNextAction({}));
}

export function* wizardOrderTemplateHandleNextSuccessSaga(/* action: TWizardNextSuccessAction */): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardOrderTemplateSteps.TemplateSelectionDataStep:
            break;
        case EWizardOrderTemplateSteps.TemplateParametersConfirmStep:
            break;
    }
}

/*
 * -------------------------------------------
 *       STORAGE
 * -------------------------------------------
 */

export function* wizardOrderTemplateLoadDataFromStorageSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams = localStorageState[basketSnapshotId];

    if (localStorageParams) {
        yield put(wizardOrderTemplateSetParamsAction(localStorageParams)); //! TODO: Check with data
    }
}

/*
 * -------------------------------------------
 *       SAVE DATA TO STORAGE
 * -------------------------------------------
 */

export function* wizardOrderTemplateStorageSaveSaga(): SagaIterator {
    const params = {};
    yield put(wizardStorageSaveStepAction({ params }));
}

/*
 * -------------------------------------------
 *              Failure Handler
 * -------------------------------------------
 */

export function* wizardOrderTemplateFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        console.error(errorCode);
    }

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title,
                    translation: translationCommon.modal,
                }),
        }),
    );
}

/*
 * -------------------------------------------
 *       Wizard Sagas
 * -------------------------------------------
 */

export function* wizardOrderTemplateSagas() {
    yield all([
        takeEvery(WIZARD_START_SUCCESS, wizardOrderTemplateStorageSaveSaga),
        takeEvery(WIZARD_META_SUCCESS, wizardOrderTemplateMetaSaga),
        takeEvery(WIZARD_ORDER_TEMPLATE_HANDLE_NEXT, wizardOrderTemplateHandleNextSaga),

        takeEvery(WIZARD_NEXT_SUCCESS, wizardOrderTemplateHandleNextSuccessSaga),
        takeEvery([WIZARD_NEXT_SUCCESS, WIZARD_BACK_SUCCESS], wizardOrderTemplateLoadDataFromStorageSaga),

        takeEvery(
            [WIZARD_BACK_FAILED, WIZARD_NEXT_FAILED, WIZARD_RESTORE_FAILED, WIZARD_START_FAILED],
            wizardOrderTemplateFailedSaga,
        ),
    ]);
}
